import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';

import { ROUTE_PATHS } from '@/app/router/paths';
import { config } from '@/app/config/env';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';

/**
 * Route-level error element.
 *
 * Catches failures React Router surfaces — a lazy chunk that fails to load
 * (common after a deploy replaces the previous build's assets), a loader
 * rejection, or a thrown response — and renders a recoverable page rather
 * than an unstyled router default.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  logger.error('Route error', { error });

  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 p-6 text-center">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">
          {isNotFound ? 'Page not found' : 'Something went wrong'}
        </h1>
        <p className="text-muted-foreground max-w-md text-sm">
          {isNotFound
            ? 'The page you are looking for does not exist or has been moved.'
            : 'This page could not be loaded. Reloading may resolve the problem.'}
        </p>
      </div>

      {/* Diagnostics stay out of production builds so internals are not
          exposed to whoever triggered the error. */}
      {config.isDevelopment && error instanceof Error && (
        <pre className="bg-muted text-muted-foreground max-w-full overflow-x-auto rounded-md p-3 text-left text-xs">
          {error.message}
        </pre>
      )}

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          variant="outline"
          onClick={() => {
            window.location.reload();
          }}
        >
          Reload page
        </Button>

        <Button asChild>
          <Link to={ROUTE_PATHS.dashboard}>Return to dashboard</Link>
        </Button>
      </div>
    </main>
  );
}
